import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Files, EnvelopeSimple, FileText, ShieldCheck, ClipboardText, ChartLine,
  Printer, Eye, Warning, Question, PaperPlaneTilt, CheckCircle,
} from '@phosphor-icons/react';
import TI from '../../components/brand/TI';
import { HaleWordmark } from '../../components/HaleBrand';
import SummaryCards from '../../components/hale-review/SummaryCards';
import AuditTimeline from '../../components/hale-review/AuditTimeline';
import DocumentChecklist from '../../components/hale-review/DocumentChecklist';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IVORY = '#F5F1E8';
const SLATE = '#0B1426';
const CORAL = '#FF4E6A';
const HALE_RED = '#E63946';
const MUTED = '#5B6577';
const RULE  = 'rgba(11,20,38,0.10)';

/* /admin/hale/samples — Generic Hale Audit Program Output Library.
 *
 * Single admin-only surface that hosts the 15 canonical sample outputs
 * the workflow produces, using ONLY synthetic data ("Sample Brand Co.").
 * Reuses existing visual primitives (SummaryCards, AuditTimeline,
 * DocumentChecklist, badge SVG endpoint, BrandWorkflowStatusCard) so
 * we don't duplicate components or copy.
 *
 * Every preview is watermarked SAMPLE — Generic Hale Audit Program
 * Output. Not a real assessment. so it can never be mistaken for a
 * real assessment leaving the platform. */

const SAMPLE_BRAND = {
  brand_name: 'Sample Brand Co.',
  founder:    'Alex Founder',
  founder_email: 'alex@samplebrand.example',
  pathway:    'Hale Reviewed™',
  services:   ['Reviewed™ pathway', 'Regulatory readiness', 'Packaging + RSL intel'],
  submitted_at: '2026-02-10T15:04:00Z',
  routing_id: 'HSR-26-0001-SAMPLE',
};

const SAMPLE_CHECKLIST = [
  { id: 'formula',   label: 'Formula / ingredient list',    status: 'complete' },
  { id: 'sds_coa',   label: 'SDS / COA',                    status: 'missing' },
  { id: 'packaging', label: 'Packaging specs',              status: 'complete' },
  { id: 'claims',    label: 'Claims copy',                  status: 'not_applicable' },
  { id: 'use',       label: 'Product use instructions',     status: 'complete' },
  { id: 'test_data', label: 'Supporting test data',         status: 'missing' },
];

const SAMPLES = [
  { id: '01', title: 'Brand Intake Confirmation',       audience: 'Brand',        format: 'Dashboard', visible: true },
  { id: '02', title: 'Hale Admin Intake Record',        audience: 'Hale Admin',   format: 'Record',    visible: false },
  { id: '03', title: 'Regulatory Queue Notification',   audience: 'AG / Internal',format: 'Email',     visible: false },
  { id: '04', title: 'AG Reviewer Intake Summary',      audience: 'AG / Internal',format: 'Record',    visible: false },
  { id: '05', title: 'Document Checklist Output',       audience: 'AG / Internal',format: 'Record',    visible: false },
  { id: '06', title: 'Scientific Audit Draft',          audience: 'AG / Internal',format: 'Record',    visible: false },
  { id: '07', title: 'Additional Information Request',  audience: 'Brand · Hale', format: 'Email',     visible: true },
  { id: '08', title: 'Scientific Output Complete',      audience: 'Hale Admin',   format: 'Record',    visible: false },
  { id: '09', title: 'Hale Program Decision Record',    audience: 'Hale Admin',   format: 'Record',    visible: false },
  { id: '10', title: 'Badge Issuance Record',           audience: 'Brand',        format: 'Badge',     visible: true },
  { id: '11', title: 'Brand Dashboard Status Update',   audience: 'Brand',        format: 'Dashboard', visible: true },
  { id: '12', title: 'Audit Trail / Timeline',          audience: 'Hale Admin',   format: 'Record',    visible: false },
  { id: '13', title: 'Email Notification Library',      audience: 'Mixed',        format: 'Email',     visible: 'partial' },
  { id: '14', title: 'Audit PDF Report Sample',         audience: 'Hale Admin',   format: 'PDF',       visible: false },
  { id: '15', title: 'Export Inventory',                audience: 'Hale Admin',   format: 'Record',    visible: false },
];

function SampleWatermark() {
  return (
    <div
      className="text-[10px] font-mono uppercase tracking-[0.25em] px-3 py-1.5 inline-block rounded-sm mb-3"
      style={{ backgroundColor: 'rgba(230,57,70,0.10)', color: HALE_RED, border: `1px solid ${HALE_RED}` }}
      data-testid="sample-watermark"
    >
      SAMPLE · Generic Hale Audit Program Output · Not a real assessment
    </div>
  );
}

function SendSampleButton({ templateId, testid }) {
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const [feedback, setFeedback] = useState(null);

  const onClick = async () => {
    setState('sending'); setFeedback(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/hale-review/samples/send-email`,
        { template_id: templateId },
        { withCredentials: true },
      );
      setState('sent');
      setFeedback(`Sent to ${r.data.sent_to}${r.data.mocked ? ' · mocked' : ''}`);
    } catch (e) {
      setState('error');
      setFeedback(e?.response?.data?.detail || 'Send failed.');
    }
  };

  return (
    <div className="mt-2.5">
      <button
        type="button"
        onClick={onClick}
        disabled={state === 'sending' || state === 'sent'}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-[10px] font-mono uppercase tracking-[0.18em] border disabled:opacity-50 transition-transform hover:-translate-y-px"
        style={{
          borderColor: state === 'sent' ? '#16624B' : (state === 'error' ? '#B0312D' : MUTED),
          color: state === 'sent' ? '#16624B' : (state === 'error' ? '#B0312D' : SLATE),
          backgroundColor: state === 'sent' ? 'rgba(22,98,75,0.06)' : 'transparent',
        }}
        data-testid={testid}
      >
        {state === 'sent'    ? <><CheckCircle size={11} weight="fill" /> Sent</>
         : state === 'sending' ? <>Sending…</>
         : state === 'error'   ? <><Warning size={11} weight="bold" /> Retry</>
         : <><PaperPlaneTilt size={11} weight="bold" /> Send to me</>}
      </button>
      {feedback && (
        <p
          className="text-[10px] mt-1.5"
          style={{ color: state === 'error' ? '#B0312D' : MUTED }}
          data-testid={`${testid}-feedback`}
        >
          {feedback}
        </p>
      )}
    </div>
  );
}

function Section({ id, title, audience, format, children }) {
  return (
    <section
      id={id}
      className="rounded-sm border p-6 mb-6"
      style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}
      data-testid={`sample-${id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>
            {id} · {audience} · {format}
          </p>
          <h3 className="text-xl font-extrabold tracking-tight mt-1"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
            {title}
          </h3>
        </div>
      </div>
      <SampleWatermark />
      {children}
    </section>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b last:border-0" style={{ borderColor: RULE }}>
      <span className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: MUTED }}>{k}</span>
      <span className="text-[13px] font-semibold text-right" style={{ color: SLATE }}>{v}</span>
    </div>
  );
}

function EmailPreview({ from, to, subject, body }) {
  return (
    <div className="rounded-sm border" style={{ borderColor: RULE, backgroundColor: IVORY }}>
      <div className="p-3 border-b" style={{ borderColor: RULE }}>
        <Row k="From" v={from} />
        <Row k="To" v={to} />
        <Row k="Subject" v={subject} />
      </div>
      <div className="p-5 text-[13px] leading-relaxed" style={{ color: SLATE, whiteSpace: 'pre-wrap' }}>
        {body}
      </div>
    </div>
  );
}

function BadgeSvgPreview({ label = 'Hale Reviewed™' }) {
  // Inline SVG mirroring the live badge endpoint output — pure preview,
  // labelled SAMPLE so it can't be mistaken for a real badge.
  return (
    <svg viewBox="0 0 360 200" width="360" height="200" xmlns="http://www.w3.org/2000/svg" role="img" aria-label={`${label} — Sample Brand Co.`}>
      <rect x="0" y="0" width="360" height="200" fill="#F5F1E8" stroke="rgba(11,20,38,0.10)" />
      <rect x="24" y="24" width="68" height="32" fill="#E63946"/>
      <text x="58" y="46" textAnchor="middle" fill="#0B0B0B"
            fontFamily='"Archivo Black",Impact,sans-serif' fontWeight="900" fontSize="22" letterSpacing="1.5">HALE</text>
      <text x="104" y="46" fill="#5B6577" fontFamily="ui-monospace,Menlo,monospace" fontSize="10" letterSpacing="2">SAMPLE · PROGRAM CERTIFICATION</text>
      <text x="24" y="100" fill="#0B1426" fontFamily='"Cormorant Garamond",serif' fontWeight="800" fontSize="32">{label}</text>
      <rect x="24" y="112" width="60" height="3" fill="#FF4E6A"/>
      <text x="24" y="142" fill="#0B1426" fontFamily="Inter,sans-serif" fontSize="14" fontWeight="600">Sample Brand Co.</text>
      <text x="24" y="160" fill="#5B6577" fontFamily="Inter,sans-serif" fontSize="11">Independent scientific review · The Agyekum Group</text>
      <text x="24" y="180" fill="#5B6577" fontFamily="ui-monospace,Menlo,monospace" fontSize="10" letterSpacing="1.5">ISSUED 2026-02-14 · ID SAMPLE01</text>
      <text x="336" y="180" textAnchor="end" fill="#5B6577" fontFamily="ui-monospace,Menlo,monospace" fontSize="10" letterSpacing="1.5">POWERED BY TOXINTEL</text>
    </svg>
  );
}

export default function HaleAuditSamplesPage() {
  const [filter, setFilter] = useState('all');
  const filtered = useMemo(() => {
    if (filter === 'all') return SAMPLES;
    if (filter === 'brand')    return SAMPLES.filter(s => s.audience.includes('Brand'));
    if (filter === 'hale')     return SAMPLES.filter(s => s.audience.includes('Hale'));
    if (filter === 'internal') return SAMPLES.filter(s => s.audience.includes('AG') || s.audience.includes('Internal'));
    return SAMPLES;
  }, [filter]);

  const handlePrint = () => window.print();

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: IVORY, color: SLATE, fontFamily: 'Inter, system-ui, sans-serif' }}
      data-testid="hale-audit-samples-page"
    >
      <Helmet><title>Hale Audit Program Samples · ToxIntel</title></Helmet>

      <header className="border-b print:hidden" style={{ borderColor: RULE }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-end gap-3">
            <Link to="/admin/hale" className="flex items-end gap-3">
              <span className="text-xl tracking-tight" style={{ color: SLATE, fontFamily: '"Cormorant Garamond", serif' }}>
                <TI color={CORAL} />
              </span>
            </Link>
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] px-2 py-1 rounded-sm"
                  style={{ backgroundColor: SLATE, color: IVORY }}>
              · Admin · Hale · Audit Program Samples
            </span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/admin/hale" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="samples-nav-back">
              ← Hale admin hub
            </Link>
            <button onClick={handlePrint}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-[11px] font-mono uppercase tracking-wider border"
                    style={{ borderColor: SLATE, color: SLATE, backgroundColor: IVORY }}
                    data-testid="samples-print">
              <Printer size={12} weight="bold" /> Export PDF (print)
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 lg:py-14">
        {/* Hero */}
        <div className="mb-8">
          <HaleWordmark size="md" />
          <p className="mt-5 text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>
            Audit program samples · generic workflow outputs
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight"
              style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
              data-testid="samples-title">
            Generic Hale audit program output library.
          </h1>
          <p className="mt-3 text-sm max-w-3xl" style={{ color: MUTED }}>
            Reference samples for every stage of the Hale × <TI color={HALE_RED} /> review workflow,
            using only synthetic data (Sample Brand Co.). Brand-facing outputs never leak internal
            reviewer notes — AG retains scientific authority, Hale retains program authority.
          </p>
        </div>

        {/* Audience filter */}
        <div className="flex flex-wrap gap-2 mb-6 print:hidden" data-testid="samples-filter">
          {[
            { id: 'all',      label: 'All' },
            { id: 'brand',    label: 'Brand-facing' },
            { id: 'hale',     label: 'Hale admin' },
            { id: 'internal', label: 'AG / ToxIntel internal' },
          ].map(t => (
            <button key={t.id} onClick={() => setFilter(t.id)}
                    className="px-3 py-1.5 rounded-sm text-[11px] font-mono uppercase tracking-wider border"
                    style={{
                      borderColor: filter === t.id ? CORAL : RULE,
                      color: filter === t.id ? CORAL : MUTED,
                      backgroundColor: filter === t.id ? 'rgba(255,78,106,0.06)' : 'transparent',
                    }}
                    data-testid={`samples-filter-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Index table */}
        <section
          className="rounded-sm border mb-10"
          style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}
          data-testid="samples-index"
        >
          <div className="p-4 border-b" style={{ borderColor: RULE }}>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>
              Index · {filtered.length} of {SAMPLES.length} samples
            </p>
          </div>
          <table className="w-full text-[12px]" data-testid="samples-table">
            <thead>
              <tr className="text-left" style={{ color: MUTED }}>
                <th className="px-4 py-2 font-mono uppercase tracking-wider text-[10px]">#</th>
                <th className="px-4 py-2 font-mono uppercase tracking-wider text-[10px]">Output</th>
                <th className="px-4 py-2 font-mono uppercase tracking-wider text-[10px]">Audience</th>
                <th className="px-4 py-2 font-mono uppercase tracking-wider text-[10px]">Format</th>
                <th className="px-4 py-2 font-mono uppercase tracking-wider text-[10px]">Brand-visible</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} className="border-t" style={{ borderColor: RULE }}>
                  <td className="px-4 py-2.5 font-mono" style={{ color: MUTED }}>{s.id}</td>
                  <td className="px-4 py-2.5">
                    <a href={`#sample-${s.id}`} className="font-semibold hover:underline" style={{ color: SLATE }} data-testid={`samples-index-link-${s.id}`}>
                      {s.title}
                    </a>
                  </td>
                  <td className="px-4 py-2.5" style={{ color: MUTED }}>{s.audience}</td>
                  <td className="px-4 py-2.5" style={{ color: MUTED }}>{s.format}</td>
                  <td className="px-4 py-2.5" style={{ color: s.visible === true ? '#16624B' : s.visible === 'partial' ? '#A07A14' : '#B0312D' }}>
                    {s.visible === true ? 'Yes' : s.visible === 'partial' ? 'Some' : 'No'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* 01 — Brand Intake Confirmation */}
        <Section id="sample-01" title="Brand Intake Confirmation" audience="Brand" format="Dashboard">
          <div className="rounded-sm border p-5" style={{ borderColor: RULE, backgroundColor: IVORY }}>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>Submission received</p>
            <h4 className="text-2xl font-extrabold mt-1" style={{ fontFamily: '"Cormorant Garamond",serif', color: SLATE }}>
              Thank you, {SAMPLE_BRAND.brand_name}.
            </h4>
            <p className="text-sm mt-2" style={{ color: MUTED }}>
              We&apos;ve received your Hale onboarding submission. Independent scientific review now begins.
            </p>
            <div className="grid sm:grid-cols-2 gap-x-8 mt-4">
              <div><Row k="Brand" v={SAMPLE_BRAND.brand_name} /><Row k="Founder" v={SAMPLE_BRAND.founder} /><Row k="Founder email" v={SAMPLE_BRAND.founder_email} /></div>
              <div><Row k="Pathway requested" v={SAMPLE_BRAND.pathway} /><Row k="Services" v={SAMPLE_BRAND.services.join(' · ')} /><Row k="Submitted" v="2026-02-10 · 15:04 UTC" /></div>
            </div>
            <p className="text-[11px] mt-4 font-mono uppercase tracking-[0.18em]" style={{ color: HALE_RED }}>STATUS · SUBMITTED</p>
            <p className="text-[12px] mt-2" style={{ color: SLATE }}>
              <b>Next:</b> Your submission is routing to the AG / <TI color={HALE_RED} /> reviewer queue. You&apos;ll receive an update when scientific review begins.
            </p>
          </div>
        </Section>

        {/* 02 — Hale Admin Intake Record */}
        <Section id="sample-02" title="Hale Admin Intake Record" audience="Hale Admin" format="Record">
          <SummaryCards items={[
            { key: 'pathway',  label: 'Pathway',         value: 'Reviewed™' },
            { key: 'services', label: 'Services',        value: 3 },
            { key: 'missing',  label: 'Missing docs',    value: 2, tone: '#A07A14' },
            { key: 'ag',       label: 'AG status',       value: 'Intake' },
            { key: 'hale',     label: 'Hale status',     value: 'New' },
          ]} testidPrefix="sample-02-summary" />
          <Row k="Brand" v={SAMPLE_BRAND.brand_name} />
          <Row k="Founder" v={SAMPLE_BRAND.founder} />
          <Row k="Pathway requested" v={SAMPLE_BRAND.pathway} />
          <Row k="Services requested" v={SAMPLE_BRAND.services.join(' · ')} />
          <Row k="Missing documents" v="SDS/COA · Supporting test data" />
          <Row k="Assigned reviewer" v="Awaiting AG queue pickup" />
          <Row k="Routing ID" v={SAMPLE_BRAND.routing_id} />
        </Section>

        {/* 03 — Regulatory Queue Notification */}
        <Section id="sample-03" title="Regulatory Queue Notification" audience="AG / Internal" format="Email">
          <EmailPreview
            from="hale-notifications@toxintel.tagc.us"
            to="regulatory@tagc.us"
            subject={`[Hale] New submission — ${SAMPLE_BRAND.brand_name}`}
            body={`A new Hale submission has been routed to the regulatory queue for scientific review.

Brand:           ${SAMPLE_BRAND.brand_name}
Pathway:         ${SAMPLE_BRAND.pathway}
Services:        ${SAMPLE_BRAND.services.join(', ')}
Documents up:    Formula, Packaging specs, Use instructions
Documents miss:  SDS/COA, Supporting test data
Routing ID:      ${SAMPLE_BRAND.routing_id}
AG status:       Intake Received
Hale status:     New Submission

Open the reviewer console to begin the scientific audit:
https://toxintel.tagc.us/assessor/hale-review

Hale × ToxIntel · scientific review partner: The Agyekum Group`}
          />
          <SendSampleButton templateId="intake" testid="sample-03-send" />
        </Section>

        {/* 04 — AG Reviewer Intake Summary */}
        <Section id="sample-04" title="AG Reviewer Intake Summary" audience="AG / Internal" format="Record">
          <Row k="Brand" v={SAMPLE_BRAND.brand_name} />
          <Row k="Category" v="Body care · supplements" />
          <Row k="Review type" v="Reviewed™ scientific governance" />
          <Row k="Submitted documents" v="Formula · Packaging specs · Use instructions · Claims copy" />
          <Row k="Missing documents" v="SDS/COA · Supporting test data" />
          <Row k="Initial risk flags" v="Fragrance compound 2A · packaging recyclability claim" />
          <Row k="Assigned reviewer" v="regulatory@tagc.us" />
          <Row k="Audit status" v="Intake Received → Document Review (pending)" />
        </Section>

        {/* 05 — Document Checklist */}
        <Section id="sample-05" title="Document Checklist Output" audience="AG / Internal" format="Record">
          <DocumentChecklist value={SAMPLE_CHECKLIST} onChange={() => {}} disabled />
        </Section>

        {/* 06 — Scientific Audit Draft (with internal markers) */}
        <Section id="sample-06" title="Scientific Audit Draft" audience="AG / Internal" format="Record">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-1" style={{ color: MUTED }}>Scope of review</p>
          <p className="text-[13px] mb-3" style={{ color: SLATE }}>
            Independent scientific audit of {SAMPLE_BRAND.brand_name} portfolio against Hale Reviewed™ pathway requirements, including ingredient
            transparency, packaging stewardship, and claims substantiation.
          </p>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-1" style={{ color: MUTED }}>Documents reviewed</p>
          <p className="text-[13px] mb-3" style={{ color: SLATE }}>Formula sheet, packaging specifications, use instructions, marketing claims dossier.</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-1" style={{ color: MUTED }}>Key observations</p>
          <ul className="text-[13px] list-disc ml-5 mb-3" style={{ color: SLATE }}>
            <li>Ingredient list is complete and meets transparency expectations.</li>
            <li>Packaging stewardship documentation is satisfactory; recyclability claim requires verification.</li>
            <li>Fragrance compound 2A flagged for supplier documentation.</li>
          </ul>
          <div className="rounded-sm border p-3 mt-4" style={{ borderColor: RULE, backgroundColor: 'rgba(11,20,38,0.04)' }}>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: HALE_RED }}>Internal reviewer notes · never shown to brand</p>
            <p className="text-[12.5px] mt-1" style={{ color: SLATE }}>Vendor has been slow on supplier docs; recommend conditional approval pending receipt within 14 days.</p>
          </div>
        </Section>

        {/* 07 — Additional Information Request */}
        <Section id="sample-07" title="Additional Information Request" audience="Brand · Hale" format="Email">
          <EmailPreview
            from="hale-notifications@toxintel.tagc.us"
            to={`${SAMPLE_BRAND.founder_email}, hale-admin@toxintel.tagc.us`}
            subject={`Hale status update — Documents Needed`}
            body={`Hello ${SAMPLE_BRAND.founder.split(' ')[0]},

Your Hale application is in scientific review. Two items are outstanding before the audit can complete:

  1. SDS / COA for ${SAMPLE_BRAND.brand_name} core formula
  2. Supporting test data referenced in your claims dossier

Please upload via your brand dashboard at your earliest convenience. Deadline (placeholder): 2026-02-28.

Status: Documents Needed

For internal Hale reference: AG has flagged fragrance compound 2A pending supplier doc receipt.`}
          />
          <SendSampleButton templateId="documents_needed" testid="sample-07-send" />
        </Section>

        {/* 08 — Scientific Output Complete */}
        <Section id="sample-08" title="Scientific Output Complete" audience="Hale Admin" format="Record">
          <Row k="Brand" v={SAMPLE_BRAND.brand_name} />
          <Row k="Review summary" v="Audit complete; minor conditional finding on fragrance compound 2A." />
          <Row k="Conditions" v="Confirm supplier documentation for fragrance compound 2A within 14 days of activation." />
          <Row k="Recommendation" v="Recommended with Conditions" />
          <p className="text-[11px] mt-4 italic" style={{ color: MUTED }}>
            AG / <TI color={HALE_RED} /> controls the scientific output. Hale retains final program approval authority.
          </p>
        </Section>

        {/* 09 — Hale Program Decision Record */}
        <Section id="sample-09" title="Hale Program Decision Record" audience="Hale Admin" format="Record">
          <Row k="AG recommendation received" v="Recommended with Conditions" />
          <Row k="Hale decision" v="Approved with Conditions" />
          <Row k="Badge pathway" v="Hale Reviewed™" />
          <Row k="Program notes" v="Conditions accepted; brand admitted to Reviewed pathway." />
          <Row k="Decision timestamp" v="2026-02-14 · 11:22 UTC" />
        </Section>

        {/* 10 — Badge Issuance Record */}
        <Section id="sample-10" title="Badge Issuance Record" audience="Brand" format="Badge">
          <div className="flex flex-wrap gap-6 items-start">
            <BadgeSvgPreview label="Hale Reviewed™" />
            <div className="flex-1 min-w-[260px]">
              <Row k="Brand" v={SAMPLE_BRAND.brand_name} />
              <Row k="Badge issued" v="Hale Reviewed™" />
              <Row k="Effective date" v="2026-02-14" />
              <Row k="Conditions" v="Fragrance compound 2A supplier doc within 14 days" />
              <Row k="Renewal interval" v="12 months (placeholder)" />
              <Row k="Surfaced on" v="/hale/dashboard · embed URL /api/hale-review/badges/{id}.svg" />
            </div>
          </div>
        </Section>

        {/* 11 — Brand Dashboard Status Update */}
        <Section id="sample-11" title="Brand Dashboard Status Update" audience="Brand" format="Dashboard">
          <div className="rounded-sm border p-5" style={{ borderColor: RULE, backgroundColor: 'rgba(22,98,75,0.06)' }}>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>Hale review status</p>
            <p className="text-2xl font-extrabold mt-2" style={{ fontFamily: '"Cormorant Garamond",serif', color: SLATE }}>
              Badge Issued · Hale Reviewed™
            </p>
            <Row k="Pathway" v={SAMPLE_BRAND.pathway} />
            <Row k="Documents needed" v="None outstanding" />
            <Row k="Reviewer questions" v="None" />
            <Row k="Hale decision" v="Approved with Conditions" />
            <Row k="Badge status" v="Active" />
            <Row k="Next action" v="Submit fragrance supplier doc within 14 days" />
          </div>
        </Section>

        {/* 12 — Audit Trail / Timeline */}
        <Section id="sample-12" title="Audit Trail / Timeline" audience="Hale Admin" format="Record">
          <AuditTimeline
            stages={['Submitted', 'Intake captured', 'Routed to regulatory@tagc.us', 'Document review', 'Additional info requested', 'Scientific review complete', 'Hale decision pending', 'Hale approved', 'Badge issued']}
            currentIndex={8}
            testid="sample-12-timeline"
          />
        </Section>

        {/* 13 — Email Notification Library */}
        <Section id="sample-13" title="Email Notification Library" audience="Mixed" format="Email">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              ['intake',            'Submission received',           'AG · Internal', 'Routed to regulatory@tagc.us with intake details.'],
              ['documents_needed',  'Documents needed',              'Brand',         'Two items outstanding — please upload via dashboard.'],
              ['review_started',    'Review started',                'Brand',         'Independent scientific review has begun.'],
              ['reviewer_question', 'Reviewer question',             'Brand',         'A reviewer has a clarification question.'],
              ['ag_complete',       'Scientific output complete',    'Hale',          'AG audit complete — Hale decision pending.'],
              ['hale_approved',     'Hale approved',                 'Brand',         'You have been admitted to Hale Reviewed™.'],
              ['hale_rejected',     'Hale rejected',                 'Brand',         'Submission not admitted — see notes.'],
              ['badge_issued',      'Badge issued',                  'Brand',         'Your Hale Reviewed™ badge is now active.'],
            ].map(([id, title, audience, body]) => (
              <div key={id} className="rounded-sm border p-3" style={{ borderColor: RULE, backgroundColor: IVORY }}>
                <p className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: CORAL }}>{audience}</p>
                <p className="text-[13px] font-semibold mt-1" style={{ color: SLATE }}>{title}</p>
                <p className="text-[12px] mt-1" style={{ color: MUTED }}>{body}</p>
                <SendSampleButton templateId={id} testid={`sample-13-send-${id}`} />
              </div>
            ))}
          </div>
        </Section>

        {/* 14 — PDF report sample (print-friendly) */}
        <Section id="sample-14" title="Audit PDF Report Sample" audience="Hale Admin" format="PDF">
          <p className="text-[11px] mb-3" style={{ color: MUTED }}>Print or save-as-PDF via the button in the top bar; this section renders cleanly on letter / A4.</p>
          <div className="rounded-sm border p-7" style={{ borderColor: RULE, backgroundColor: 'white' }}>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: HALE_RED }}>SAMPLE — Generic Hale Audit Program Output · Not a real assessment</p>
            <h4 className="text-2xl font-extrabold mt-2" style={{ fontFamily: '"Cormorant Garamond",serif', color: SLATE }}>
              Hale Reviewed™ scientific audit · {SAMPLE_BRAND.brand_name}
            </h4>
            <p className="text-[12px] mt-1" style={{ color: MUTED }}>
              Cover · Scope · Documents reviewed · Findings · Recommendation · Conditions · Audit trail · Disclaimer.
            </p>
            <div className="mt-4">
              <Row k="Scope"             v="Hale Reviewed™ pathway · ingredient, packaging, claims" />
              <Row k="Documents reviewed" v="Formula, Packaging specs, Use instructions, Claims dossier" />
              <Row k="Findings"          v="Audit complete; minor conditional finding on fragrance compound 2A." />
              <Row k="Recommendation"    v="Recommended with Conditions" />
              <Row k="Conditions"        v="Supplier documentation for fragrance compound 2A within 14 days." />
              <Row k="Audit trail"       v="Submitted → AG review → Output complete → Hale approved → Badge issued" />
            </div>
            <p className="text-[10px] italic mt-4" style={{ color: MUTED }}>
              Disclaimer: This is a generic sample output. Not legally final. AG / ToxIntel retains scientific authority; Hale retains program authority.
            </p>
          </div>
        </Section>

        {/* 15 — Export Inventory */}
        <Section id="sample-15" title="Export Inventory" audience="Hale Admin" format="Record">
          <p className="text-[12px] mb-3" style={{ color: MUTED }}>
            This same index also appears at the top of the page for quick navigation. Reproduced here for export.
          </p>
          <ul className="text-[12.5px]" style={{ color: SLATE }}>
            {SAMPLES.map(s => (
              <li key={s.id} className="py-1 border-b" style={{ borderColor: RULE }}>
                <span className="font-mono" style={{ color: MUTED }}>{s.id}</span> · <b>{s.title}</b> ·{' '}
                <span style={{ color: MUTED }}>{s.audience} · {s.format} · brand-visible: {s.visible === true ? 'Yes' : s.visible === 'partial' ? 'Some' : 'No'}</span>
              </li>
            ))}
          </ul>
        </Section>

        <p className="text-[11px] mt-10 text-center" style={{ color: MUTED }}>
          All samples on this page use synthetic data only. No real client or brand data is referenced.
        </p>
      </main>
    </div>
  );
}
